/**
 * This context is used to display modals in our application
 * This is based on this SO answer by Dan Abramov: https://stackoverflow.com/a/35641680
 * It has been adapted to use React Context instead of Redux
 */

import React, { useContext, useState } from "react";

import StoreSelectorModal, {
  StoreSelectorModalProps,
} from "../components/modals/StoreSelectorModal";

import PageVeil from "../components/PageVeil";

type ModalOptions = {
  modalToShow:
    | "LOGOUT_MODAL"
    | "STORE_SELECTOR_MODAL"
    | "NPS_INFORMATION_MODAL"
    | "INFORMATION_MODAL";
  modalProps: StoreSelectorModalProps;
};
type ModalTypes = ModalOptions["modalToShow"];
type ModalProps = ModalOptions["modalProps"];

const MODAL_COMPONENTS = {
  STORE_SELECTOR_MODAL: StoreSelectorModal,
} as Record<ModalTypes, React.ComponentType<ModalProps>>;

type ModalRootProps = {
  modalOptions: ModalOptions | null;
};

const ModalRoot: React.FunctionComponent<ModalRootProps> = ({
  modalOptions,
}: ModalRootProps) => {
  if (!modalOptions) {
    return null;
  }
  const ModalToShow = MODAL_COMPONENTS[modalOptions.modalToShow];

  return (
    //onClick={() => modalOptions?.modalProps.onCancel()}
    <PageVeil
      onClick={(event) => {
        if (!(event?.target as Element)?.closest(".modal")) {
          modalOptions?.modalProps.onCancel();
        }
      }}
    >
      <ModalToShow {...modalOptions.modalProps} />
    </PageVeil>
  );
};

type ModalsContextType = {
  showModal: (modalToShow: ModalOptions) => void;
  closeModal: () => void;
  isModalOpen: boolean;
};

const ModalsContext = React.createContext<ModalsContextType>({
  showModal: () => {},
  closeModal: () => {},
  isModalOpen: false,
});

type ModalsProviderProps = {
  children: React.ReactNode;
};

export const ModalsProvider: React.FunctionComponent<ModalsProviderProps> = ({
  children,
}: ModalsProviderProps) => {
  const [modalOptions, setModalOptions] = useState<ModalOptions | null>(null);

  const showModal = (newModalOptions: ModalOptions) => {
    setModalOptions(newModalOptions);
  };

  const closeModal = () => {
    setModalOptions(null);
  };

  const isModalOpen = modalOptions !== null;
  return (
    <ModalsContext.Provider value={{ showModal, closeModal, isModalOpen }}>
      {children}
      <ModalRoot modalOptions={modalOptions} />
    </ModalsContext.Provider>
  );
};

export const useModals: () => ModalsContextType = () =>
  useContext(ModalsContext);
